import { DateRange } from '@/components/DateRangePicker';
import {
    Chart, DataObj,
    KPI
} from '@/contexts/AdminContext/dashboardProvider';
import { runSelect } from '@/utils/run_select';
import { Tooltip as BustlyTooltip } from '@common/index';
import clsx from 'clsx';
import dayjs from 'dayjs';
import {
    ArrowUpDown,
    Check,
    ChevronDown,
    ChevronLeft, ChevronRight,
    Copy,
    Download,
    Filter,
    Loader2,
    Search,
    X
} from 'lucide-react';
import React, {
    useEffect,
    useMemo, useRef,
    useState
} from 'react';
import { createPortal } from 'react-dom';
import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis, YAxis
} from 'recharts';
import { replaceCreatedAtFilter } from '../../utils/replaceCreatedAtFilter';
import styles from './BreakdownModal.module.scss';
import DataSourceIcons from './DataSourceIcons';

interface BreakdownModalProps {
    isOpen: boolean;
    onClose: () => void;
    item: KPI | Chart | null;
    type: 'kpi' | 'chart';
    dateRange?: DateRange;
}

export const BreakdownModal: React.FC<BreakdownModalProps> = ({
    isOpen, onClose, item, type, dateRange
}) => {
    // Early return before any hooks
    if (!isOpen || !item) return null;

    const [activeTab, setActiveTab] = useState('trends');
    const [isQueryOpen, setIsQueryOpen] = useState(false);
    const [isCopied, setIsCopied] = useState(false);

    // Data fetching state
    const [trendDataFromSql, setTrendDataFromSql] = useState<DataObj>([]);
    const [rawDataFromSql, setRawDataFromSql] = useState<DataObj>([]);
    const [comparisonDataFromSql, setComparisonDataFromSql] = useState<DataObj>([]);
    const [isTrendLoading, setIsTrendLoading] = useState(false);
    const [isRawDataLoading, setIsRawDataLoading] = useState(false);
    const [isComparisonLoading, setIsComparisonLoading] = useState(false);

    // Filter State
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const filterRef = useRef<HTMLDivElement>(null);
    const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({});

    // Table State
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' as 'asc' | 'desc' });
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
                setIsFilterOpen(false);
            }
        };

        if (isFilterOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isFilterOpen]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // Helper function to calculate previous period
    const getPreviousRange = (startDate: Date, endDate: Date): { startDate: Date; endDate: Date } => {
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const diffDays = end.diff(start, 'day');
        const prevEnd = start.subtract(1, 'day');
        const prevStart = prevEnd.subtract(diffDays, 'day');
        return { startDate: prevStart.toDate(), endDate: prevEnd.toDate() };
    };

    // Fetch trend data and raw data when modal opens
    useEffect(() => {
        if (!isOpen || !item) return;

        const fetchData = async () => {
            // Get SQL queries
            const trendSql = type === 'kpi' ? (item as KPI).trend_sql : null;
            const rawSql = type === 'kpi' ? (item as KPI).raw_data_sql : (item as Chart).raw_data_sql;

            // Helper function to fetch trend data
            const fetchTrendData = async () => {
                if (!trendSql) return;
                setIsTrendLoading(true);
                try {
                    const query = dateRange?.startDate && dateRange?.endDate
                        ? replaceCreatedAtFilter(trendSql, dateRange.startDate, dateRange.endDate)
                        : trendSql;
                    const { data, error } = await runSelect(query);
                    if (!error && data) {
                        setTrendDataFromSql(data as DataObj);
                    }
                } finally {
                    setIsTrendLoading(false);
                }
            };

            // Helper function to fetch comparison data
            const fetchComparisonData = async () => {
                if (!trendSql || !dateRange?.startDate || !dateRange?.endDate) return;
                setIsComparisonLoading(true);
                try {
                    const prevRange = getPreviousRange(dateRange.startDate, dateRange.endDate);
                    const comparisonSql = replaceCreatedAtFilter(trendSql, prevRange.startDate, prevRange.endDate);
                    const { data, error } = await runSelect(comparisonSql);
                    if (!error && data) {
                        setComparisonDataFromSql(data as DataObj);
                    }
                } finally {
                    setIsComparisonLoading(false);
                }
            };

            // Helper function to fetch raw data
            const fetchRawData = async () => {
                if (!rawSql) return;
                setIsRawDataLoading(true);
                try {
                    const query = dateRange?.startDate && dateRange?.endDate
                        ? replaceCreatedAtFilter(rawSql, dateRange.startDate, dateRange.endDate)
                        : rawSql;
                    const { data, error } = await runSelect(query);
                    if (!error && data) {
                        setRawDataFromSql(data as DataObj);
                    }
                } finally {
                    setIsRawDataLoading(false);
                }
            };

            // Execute all requests in parallel
            await Promise.all([
                fetchTrendData(),
                fetchComparisonData(),
                fetchRawData()
            ]);
        };

        fetchData();
    }, [isOpen, item, type, dateRange?.startDate, dateRange?.endDate]);

    // Check if trend_sql and raw_data_sql exist
    const hasTrendSql = type === 'kpi' ? !!(item as KPI).trend_sql : false;
    const hasRawDataSql = type === 'kpi' ? !!(item as KPI).raw_data_sql : !!(item as Chart).raw_data_sql;

    // Get metric info
    const metricInfo = {
        formula: item.formula,
        details: item.sql_detail ? [item.sql_detail] : [],
        query: item.sql
    };

    // Helper function to format column headers
    const formatHeader = (key: string): string => {
        return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    // Helper function to identify date column from data row
    const findDateColumn = (row: any): string | null => {
        if (!row) return null;

        const keys = Object.keys(row);

        // First, try to find columns with date/time in name
        const dateNameKey = keys.find(key =>
            key.toLowerCase().includes('date')
            || key.toLowerCase().includes('time')
            || key.toLowerCase() === 'name' // for already aggregated data
        );

        if (dateNameKey) return dateNameKey;

        // Otherwise, check values to see which looks like a date
        for (const key of keys) {
            if (isDateTime(row[key])) {
                return key;
            }
        }

        // Fallback to first column
        return keys[0] || null;
    };

    // Helper function to find numeric column (excluding date column)
    const findNumericColumn = (row: any, excludeColumn: string | null): string | null => {
        if (!row) return null;

        const keys = Object.keys(row).filter(key => key !== excludeColumn);

        // First, try to find 'value' column
        if (keys.includes('value')) return 'value';

        // Otherwise, find first column with numeric value
        for (const key of keys) {
            const val = row[key];
            if (val !== null && val !== undefined && val !== '' && !isNaN(parseFloat(val))) {
                return key;
            }
        }

        // Fallback to first non-date column
        return keys[0] || null;
    };

    // Helper function to check if a value looks like a date/time
    const isDateTime = (value: any): boolean => {
        if (!value) return false;
        const str = String(value);

        // Check if it matches common date/time patterns
        const datePatterns = [
            /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // YYYY-MM-DDTHH:MM:SS
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}/, // YYYY-MM-DDTHH:MM:SS.sss
            /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/, // YYYY-MM-DD HH:MM:SS
            /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d{3}$/, // YYYY-MM-DD HH:MM:SS.sss
        ];

        return datePatterns.some(pattern => pattern.test(str));
    };

    // Helper function to format date/time for display
    const formatDateTime = (value: any): string => {
        if (!value) return String(value);

        const date = dayjs(value);
        if (!date.isValid()) return String(value);

        // Format based on whether it has time component
        const str = String(value);
        if (str.includes('T') || str.includes(':')) {
            // Has time - show date and time
            return date.tz(dayjs.tz.guess()).format('YYYY-MM-DD HH:mm:ss');
        } else {
            // Date only
            return date.tz(dayjs.tz.guess()).format('YYYY-MM-DD');
        }
    };

    // Generate trend data based on granularity
    const trendData = useMemo(() => {
        // Use data from trend_sql if available
        if (trendDataFromSql.length > 0) {
            // Identify date and numeric columns from first row
            const firstRow = trendDataFromSql[0];
            const dateCol = findDateColumn(firstRow);
            const valueCol = findNumericColumn(firstRow, dateCol);

            return trendDataFromSql.map((row: any) => ({
                name: dateCol ? row[dateCol] : Object.values(row)[0],
                value: valueCol ? parseFloat(row[valueCol]) || 0 : parseFloat(Object.values(row)[1] as string) || 0,
            }));
        }

        // Fallback to original data
        const data = item.data as DataObj;
        if (!Array.isArray(data) || data.length === 0) return [];

        // Group data by day
        const grouped = new Map<string, number>();

        data.forEach((row: any) => {
            const dateCol = findDateColumn(row);

            if (dateCol) {
                const date = new Date(row[dateCol]);
                const key = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

                const valueCol = findNumericColumn(row, dateCol);
                const value = valueCol ? parseFloat(row[valueCol]) || 0 : 0;

                grouped.set(key, (grouped.get(key) || 0) + value);
            }
        });

        return Array.from(grouped.entries()).map(([name, value]) => ({
            name,
            value: Math.round(value * 100) / 100,
        }));
    }, [trendDataFromSql, item, type]);

    // Generate comparison data (previous period)
    const comparisonData = useMemo(() => {
        if (comparisonDataFromSql.length > 0) {
            // Identify date and numeric columns from first row
            const firstRow = comparisonDataFromSql[0];
            const dateCol = findDateColumn(firstRow);
            const valueCol = findNumericColumn(firstRow, dateCol);

            return comparisonDataFromSql.map((row: any) => ({
                name: dateCol ? row[dateCol] : Object.values(row)[0],
                previousValue: valueCol ? parseFloat(row[valueCol]) || 0 : parseFloat(Object.values(row)[1] as string) || 0,
            }));
        }
        return [];
    }, [comparisonDataFromSql]);

    // Merge current and comparison data for Comparison view
    const mergedChartData = useMemo(() => {

        if (activeTab === 'comparison' && comparisonData.length > 0) {
            // Align data by index position (most recent to oldest)
            // This assumes both datasets are in chronological order
            const merged = trendData.map((currentItem, index) => {
                // Match from the end (most recent data)
                const prevIndex = comparisonData.length - 1 - index;
                const matchingPrev = prevIndex >= 0 ? comparisonData[prevIndex] : null;
                return {
                    ...currentItem,
                    previousValue: matchingPrev ? matchingPrev.previousValue : null,
                };
            });
            return merged;
        }
        return trendData;
    }, [trendData, comparisonData, activeTab]);

    // Raw data for table
    const rawData = useMemo(() => {
        // Only use data from raw_data_sql
        // Don't fall back to item.data as it's aggregated data, not raw data
        if (rawDataFromSql.length > 0) {
            return rawDataFromSql;
        }

        return [];
    }, [rawDataFromSql]);

    // Generate dynamic filter options based on raw data
    const filterOptions = useMemo(() => {
        if (rawData.length === 0) return {};

        const options: Record<string, string[]> = {};

        // Get all column names
        const columns = Object.keys(rawData[0] || {});

        // For each column, check if it has categorical data (low cardinality)
        columns.forEach(column => {
            const uniqueValues = new Set(rawData.map(row => String(row[column])));

            // If column has less than 20 unique values, it's likely categorical
            if (uniqueValues.size > 1 && uniqueValues.size < 20) {
                options[column] = Array.from(uniqueValues).sort();
            }
        });

        return options;
    }, [rawData]);

    // Filtered & Sorted Table Data
    const processedTableData = useMemo(() => {
        let data = [...rawData];

        if (searchTerm) {
            data = data.filter(row =>
                Object.values(row).some(val =>
                    String(val).toLowerCase().includes(searchTerm.toLowerCase())));
        }

        // Apply dynamic filters
        Object.entries(activeFilters).forEach(([column, selectedValues]) => {
            if (selectedValues && selectedValues.length > 0) {
                data = data.filter(item => {
                    const value = String(item[column]);
                    return selectedValues.includes(value);
                });
            }
        });

        if (sortConfig.key) {
            data.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return data;
    }, [rawData, searchTerm, sortConfig.key, sortConfig.direction, activeFilters]);

    const totalPages = Math.ceil(processedTableData.length / itemsPerPage);
    const paginatedData = processedTableData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handleSort = (key: string) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handleExport = () => {
        if (processedTableData.length === 0) return;

        const headers = Object.keys(processedTableData[0]);
        const csvContent = [
            headers.join(','),
            ...processedTableData.map(row =>
                headers.map(header => row[header]).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `${item.title.replace(/\s+/g, '_')}_data.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleFilterChange = (category: string, value: string) => {
        setActiveFilters((prev) => {
            const list = (prev as any)[category] || [];
            const next = list.includes(value)
                ? list.filter((v: string) => v !== value)
                : [...list, value];
            return { ...prev, [category]: next };
        });
        setCurrentPage(1);
    };

    // SQL syntax highlighting helper
    const highlightSQL = (sql: string) => {
        let parts = [];
        let lastIndex = 0;
        let match;

        // First, split by keywords and strings
        const combinedPattern = /\b(SELECT|FROM|WHERE|GROUP BY|ORDER BY|AS|AND|OR|SUM|COUNT|AVG|CAST|ROUND|NULLIF|CASE|WHEN|THEN|ELSE|END|DISTINCT|LIMIT|FILTER|JOIN|LEFT JOIN|RIGHT JOIN|INNER JOIN|ON|HAVING|UNION|ALL|EXISTS|IN|IS|NOT|LIKE|BETWEEN)\b|'([^']*)'/g;

        while ((match = combinedPattern.exec(sql)) !== null) {
            // Add text before the match
            if (match.index > lastIndex) {
                parts.push({ text: sql.slice(lastIndex, match.index), type: 'text' });
            }

            // Add the matched keyword or string
            if (match[1]) {
                parts.push({ text: match[1], type: 'keyword' });
            } else if (match[2]) {
                parts.push({ text: `'${match[2]}'`, type: 'string' });
            }

            lastIndex = combinedPattern.lastIndex;
        }

        // Add remaining text
        if (lastIndex < sql.length) {
            parts.push({ text: sql.slice(lastIndex), type: 'text' });
        }

        return parts.map((part, i) => {
            if (part.type === 'keyword') {
                return <span key={i} className={styles.sqlKeyword}>{part.text}</span>;
            } else if (part.type === 'string') {
                return <span key={i} className={styles.sqlString}>{part.text}</span>;
            }
            return <span key={i}>{part.text}</span>;
        });
    };

    // Custom Tooltip for Chart
    const CustomTooltip = ({
        active, payload, label 
    }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className={styles.tooltip}>
                    <p className={styles.tooltipLabel}>{label}</p>
                    {payload.map((entry: any, index: number) => (
                        <div key={index} className={styles.tooltipItem}>
                            <div
                                className={styles.tooltipColor}
                                style={{ backgroundColor: entry.color }}
                            />
                            <span className={styles.tooltipName}>
                                {entry.name}
                                :
                            </span>
                            <span className={styles.tooltipValue}>
                                {entry.value.toLocaleString()}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    return createPortal(
        <div
            className={styles.overlay}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className={styles.container}>
                {/* Top Navigation Bar */}
                <div className={styles.navbar}>
                    <div className={styles.navLeft}>
                        <h1 className={styles.navTitle}>{item.title}</h1>
                    </div>

                    <div className={styles.navRight}>
                        <button onClick={onClose} className={styles.navButton}>
                            <X size={20} strokeWidth={2} />
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div className={styles.content}>
                    {/* Metric Info Section */}
                    <div className={styles.metricCard}>
                        <div className={styles.metricCardHeader}>
                            <h3 className={styles.metricCardTitle}>Metric Definition</h3>
                            {item.sources && item.sources.length > 0 ? (
                                <div className={styles.sourceBadge}>
                                    <span className={styles.sourceLabel}>Source</span>
                                    <div className={styles.sourceContent}>
                                        <DataSourceIcons sources={item.sources} />
                                        <span className={styles.sourceText}>
                                            {item.sources.map(s =>
                                                s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}
                                        </span>
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <div className={styles.metricCardBody}>
                            <div className={styles.metricGrid}>
                                {/* Formula Section */}
                                <div>
                                    <span className={styles.metricLabel}>Calculation Formula</span>
                                    <div className={styles.metricFormula}>
                                        <p className={styles.formulaText}>
                                            {metricInfo.formula || 'No formula available'}
                                        </p>
                                    </div>
                                </div>

                                {/* Details Section */}
                                {metricInfo.details && metricInfo.details.length > 0 ? (
                                    <div>
                                        <span className={styles.metricLabel}>Key Details</span>
                                        <ul className={styles.detailList}>
                                            {metricInfo.details.map((detail, idx) => (
                                                <li key={idx} className={styles.detailItem}>
                                                    <div className={styles.detailBullet} />
                                                    <span className={styles.detailText}>{detail}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : null}
                            </div>

                            {/* Collapsible Query Section */}
                            {metricInfo.query ? (
                                <div className={styles.querySection}>
                                    <button
                                        onClick={() => setIsQueryOpen(!isQueryOpen)}
                                        className={styles.queryToggle}
                                    >
                                        <span>Query</span>
                                        <div className={clsx(styles.chevronWrapper, isQueryOpen && styles.chevronOpen)}>
                                            <ChevronDown size={14} />
                                        </div>
                                    </button>

                                    <div
                                        className={clsx(
                                            styles.queryContent,
                                            isQueryOpen && styles.queryContentOpen
                                        )}
                                    >
                                        <div className={styles.queryContentInner}>
                                            <div className={styles.codeBlock}>
                                                <div className={styles.codeActions}>
                                                    <button
                                                        className={styles.copyButton}
                                                        title="Copy SQL"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigator.clipboard.writeText(metricInfo.query);
                                                            setIsCopied(true);
                                                            setTimeout(() => setIsCopied(false), 2000);
                                                        }}
                                                    >
                                                        {isCopied ? <Check size={14} /> : <Copy size={14} />}
                                                    </button>
                                                </div>
                                                <pre className={styles.codeContent}>
                                                    {highlightSQL(metricInfo.query)}
                                                </pre>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className={styles.mainGrid}>
                        {/* Left Column: Chart & Controls */}
                        <div className={styles.chartColumn}>
                            {/* Main Visualization Card - Only show if has trend_sql */}
                            {hasTrendSql ? (
                                <div className={styles.chartCard}>
                                    <div className={styles.chartHeader}>
                                        <div className={styles.tabGroup}>
                                            {['Trends', 'Comparison'].map((tab) => (
                                                <button
                                                    key={tab}
                                                    onClick={() => setActiveTab(tab.toLowerCase())}
                                                    className={clsx(
                                                        styles.tabButton,
                                                        activeTab === tab.toLowerCase() && styles.tabButtonActive
                                                    )}
                                                >
                                                    {tab}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Chart Title */}
                                    <div className={styles.chartTitleRow}>
                                        <h3 className={styles.chartTitle}>
                                            {item.title}
                                            {' '}
                                            -
                                            {' '}
                                            {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
                                        </h3>
                                    </div>

                                    <div className={styles.chartContainer}>
                                        {isTrendLoading || isComparisonLoading ? (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    height: '100%',
                                                    flexDirection: 'column',
                                                    gap: '16px'
                                                }}
                                            >
                                                <Loader2 size={32} className={styles.spin} style={{ color: '#1A162F' }} />
                                                <p style={{ color: '#667085', fontSize: '14px' }}>
                                                    {activeTab === 'comparison' ? 'Loading comparison data...' : 'Loading trend data...'}
                                                </p>
                                            </div>
                                        ) : (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <AreaChart
                                                    data={mergedChartData}
                                                    margin={{
                                                        top: 10, right: 30, left: 0, bottom: 0
                                                    }}
                                                >
                                                    <defs>
                                                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.2} />
                                                            <stop offset="95%" stopColor="#7C3AED" stopOpacity={0} />
                                                        </linearGradient>
                                                        <linearGradient id="colorPrev" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor="#9CA3AF" stopOpacity={0.2} />
                                                            <stop offset="95%" stopColor="#9CA3AF" stopOpacity={0} />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                                                    <XAxis
                                                        dataKey="name"
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tick={{ fill: '#667085', fontSize: 12 }}
                                                        dy={10}
                                                    />
                                                    <YAxis
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tick={{ fill: '#667085', fontSize: 12 }}
                                                        dx={-10}
                                                    />
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <Legend verticalAlign="top" height={36} />

                                                    <Area
                                                        type="monotone"
                                                        dataKey="value"
                                                        name="Current Period"
                                                        stroke="#7C3AED"
                                                        strokeWidth={3}
                                                        fillOpacity={1}
                                                        fill="url(#colorValue)"
                                                    />

                                                    {activeTab === 'comparison' && (
                                                    <Area
                                                        type="monotone"
                                                        dataKey="previousValue"
                                                        name="Previous Period"
                                                        stroke="#9CA3AF"
                                                        strokeWidth={2}
                                                        strokeDasharray="5 5"
                                                        fillOpacity={0.5}
                                                        fill="url(#colorPrev)"
                                                    />
                                                    )}
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        )}
                                    </div>
                                </div>
                            ) : null}

                            {/* Breakdown Engine / Data Table - Only show if has raw_data_sql */}
                            {hasRawDataSql ? (
                                <div className={styles.tableCard}>
                                    <div className={styles.tableHeader}>
                                        <div>
                                            <h3 className={styles.tableTitle}>Raw Data</h3>
                                            <p
                                                style={{
                                                    fontSize: '12px',
                                                    color: '#667085',
                                                    marginTop: '4px',
                                                    fontWeight: 'normal'
                                                }}
                                            >
                                                Only the first 100 records are shown by default.
                                            </p>
                                        </div>

                                        <div className={styles.tableActions}>
                                            {/* Search */}
                                            <div className={styles.searchWrapper}>
                                                <div className={styles.searchIcon}>
                                                    <Search size={16} strokeWidth={2} />
                                                </div>
                                                <input
                                                    type="text"
                                                    placeholder="Search..."
                                                    value={searchTerm}
                                                    onChange={(e) => {
                                                        setSearchTerm(e.target.value);
                                                        setCurrentPage(1);
                                                    }}
                                                    className={styles.searchInput}
                                                />
                                            </div>

                                            {/* Filter Button */}
                                            <div className={styles.filterWrapper} ref={filterRef}>
                                                <BustlyTooltip content="Filter">
                                                    <button
                                                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                                                        className={clsx(
                                                            styles.iconButton,
                                                            isFilterOpen && styles.iconButtonActive
                                                        )}
                                                    >
                                                        <Filter size={18} strokeWidth={2} />
                                                    </button>
                                                </BustlyTooltip>

                                                {/* Filter Dropdown */}
                                                {isFilterOpen ? (
                                                    <div className={styles.filterDropdown}>
                                                        <div className={styles.filterContent}>
                                                            {Object.entries(filterOptions).map(([column, values]) => (
                                                                <div key={column}>
                                                                    <label className={styles.filterLabel}>{formatHeader(column)}</label>
                                                                    <div className={styles.filterOptions}>
                                                                        {values.map(value => (
                                                                            <div
                                                                                key={value}
                                                                                className={styles.filterOption}
                                                                                onClick={() => handleFilterChange(column, value)}
                                                                            >
                                                                                <div
                                                                                    className={clsx(
                                                                                        styles.checkbox,
                                                                                        (activeFilters[column] || []).includes(value) && styles.checkboxChecked
                                                                                    )}
                                                                                >
                                                                                    {(activeFilters[column] || []).includes(value)
                                                                                    && <Check size={12} className="text-white" />}
                                                                                </div>
                                                                                <span className={styles.filterOptionText}>{value}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                            {Object.keys(filterOptions).length === 0 && (
                                                            <div
                                                                style={{
                                                                    padding: '16px',
                                                                    textAlign: 'center',
                                                                    color: '#667085',
                                                                    fontSize: '14px'
                                                                }}
                                                            >
                                                                No categorical columns available for filtering
                                                            </div>
                                                            )}
                                                        </div>

                                                        <div className={styles.filterFooter}>
                                                            <button
                                                                className={styles.filterClear}
                                                                onClick={() => setActiveFilters({})}
                                                            >
                                                                Clear All
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : null}
                                            </div>

                                            {/* Export Button */}
                                            <button
                                                onClick={handleExport}
                                                className={styles.exportButton}
                                            >
                                                <Download size={16} strokeWidth={2} />
                                                Export CSV
                                            </button>
                                        </div>
                                    </div>

                                    <div className={styles.tableWrapper}>
                                        {isRawDataLoading ? (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    minHeight: '300px',
                                                    flexDirection: 'column',
                                                    gap: '16px',
                                                }}
                                            >
                                                <Loader2 size={32} className={styles.spin} style={{ color: '#1A162F' }} />
                                                <p style={{ color: '#667085', fontSize: '14px' }}>Loading raw data...</p>
                                            </div>
                                        ) : (
                                            <table className={styles.table}>
                                                <thead className={styles.tableHead}>
                                                    <tr>
                                                        {Object.keys(rawData[0] || {}).map((key) => (
                                                            <th
                                                                key={key}
                                                                className={styles.tableHeadCell}
                                                                onClick={() => handleSort(key)}
                                                            >
                                                                <div className={styles.tableHeaderContent}>
                                                                    {formatHeader(key)}
                                                                    <ArrowUpDown size={12} strokeWidth={2} />
                                                                </div>
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody className={styles.tableBody}>
                                                    {paginatedData.map((row, i) => (
                                                        <tr key={i} className={styles.tableRow}>
                                                            {Object.keys(rawData[0] || {}).map((key) => (
                                                                <td key={key} className={styles.tableCell}>
                                                                    {isDateTime(row[key]) ? formatDateTime(row[key]) : String(row[key])}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                    {paginatedData.length === 0 && (
                                                    <tr>
                                                        <td
                                                            colSpan={Object.keys(rawData[0] || {}).length}
                                                            className={styles.tableEmpty}
                                                        >
                                                            {!isRawDataLoading && rawDataFromSql.length === 0
                                                                ? 'No raw data available'
                                                                : 'No data found matching your search.'
                                                            }
                                                        </td>
                                                    </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>

                                    {/* Pagination */}
                                    <div className={styles.pagination}>
                                        <span className={styles.paginationInfo}>
                                            Showing 
                                            {' '}
                                            <span className={styles.paginationNumbers}>
                                                {Math.min((currentPage - 1) * itemsPerPage + 1, processedTableData.length)}
                                            </span>
                                            {' '}
                                            to 
                                            {' '}
                                            <span className={styles.paginationNumbers}>
                                                {Math.min(currentPage * itemsPerPage, processedTableData.length)}
                                            </span>
                                            {' '}
                                            of 
                                            {' '}
                                            <span className={styles.paginationNumbers}>
                                                {processedTableData.length}
                                            </span>
                                            {' '}
                                            results
                                        </span>
                                        <div className={styles.paginationButtons}>
                                            <button
                                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                disabled={currentPage === 1}
                                                className={styles.paginationButton}
                                            >
                                                <ChevronLeft size={18} strokeWidth={2.5} />
                                            </button>
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                let p = i + 1;
                                                if (totalPages > 5 && currentPage > 3) {
                                                    p = currentPage - 2 + i;
                                                }
                                                if (p > totalPages) return null;

                                                return (
                                                    <button
                                                        key={p}
                                                        onClick={() => setCurrentPage(p)}
                                                        className={clsx(
                                                            styles.paginationPage,
                                                            currentPage === p && styles.paginationPageActive
                                                        )}
                                                    >
                                                        {p}
                                                    </button>
                                                );
                                            })}
                                            <button
                                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                disabled={currentPage === totalPages}
                                                className={styles.paginationButton}
                                            >
                                                <ChevronRight size={18} strokeWidth={2.5} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default BreakdownModal;
